import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useContext,
  createContext,
  useCallback,
  FC,
  PropsWithChildren,
} from 'react'
import { useStripe, useElements, Elements } from '@stripe/react-stripe-js'
import { loadStripe, Stripe, StripeElements, StripeElementsOptions } from '@stripe/stripe-js'
import { CompanyCard, PromotionalCode } from '@leadgen/models/client'
import config from '../../../config'
import {
  Control,
  FieldValues,
  FormState,
  SubmitHandler,
  useForm,
  UseFormReset,
  useWatch,
} from 'react-hook-form'
import subscribeForm from '../../../forms/subscribeForm'
import unsubscribeForm from '../../../forms/unsubscribeForm'
import topupForm from '../../../forms/topupForm'
import monthlyCreditLimitForm from '../../../forms/monthlyCreditLimitForm'
import promoCodeForm from '../../../forms/promoCodeForm/promoCodeForm'
import useCallbackDebouncedAsync from '../../../hooks/shared/useCallbackDebouncedAsync'
import useStableCallback from '../../../hooks/shared/useStableCallback'
import { useAutoSave } from '../../../hooks/form/useAutosave'
import { useStateWithRef } from '../../../hooks/shared/useStateWithRef'
import { useSelector, useDispatch } from '../../../redux/hooks'
import companySelectors from '../../../redux/modules/company/selectors'
import companyActions from '../../../redux/modules/company/actions'
import authSelectors from '../../../redux/modules/auth/selectors'
import _ from 'lodash'
import { useTheme } from '@mui/material'
import companyService from '../../../services/companyService'
import { store } from '../../../redux/store'
import { useEffectUntil } from '../../../hooks/shared/useEffectUntil'
import { useDeleteCompanyCardForm } from '../../../forms/deleteCompanyCardForm/useDeleteCompanyCardForm'
import { useSwitchCompanyCardForm } from '../../../forms/switchCompanyCardForm/useSwitchCompanyCardForm'
import { useSnackbar } from '../SnackbarProvider'
import useCallbackDebounced from '../../../hooks/shared/useCallbackDebounced'
import { useSelectorWithMidnightRefresh } from '../../../hooks/shared/useSelectorWithMidnightRefresh'
import { usePollAfter } from '../../../hooks/shared/usePollAfter'

let stripePromise: Promise<Stripe | null>

const getStripe = async () => {
  if (!stripePromise) {
    stripePromise = loadStripe(config.stripeSecret)
  }
  return stripePromise
}

const withStripeProvider =
  <P extends object>(Component: React.ComponentType<P>) =>
  // eslint-disable-next-line react/display-name
  (props: P) => {
    const theme = useTheme()
    return (
      <Elements
        stripe={getStripe()}
        options={
          {
            mode: 'subscription',
            amount: 0,
            currency: 'gbp',
            paymentMethodCreation: 'manual',
            payment_method_types: ['card'],
            appearance: {
              rules: {
                // This targets the input on focus and disables the default blue outline
                '.Input': {
                  borderColor: '#c6c7cb',
                },
                '.Input:focus': {
                  outline: 'none',
                  boxShadow: 'none',
                  borderColor: theme.palette.primary.main,
                },
                '.Input:hover': {
                  borderColor: 'black',
                },
                '.Input:hover:focus': {
                  borderColor: theme.palette.primary.main,
                },
              },
              variables: {
                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
              },
            },
          } as StripeElementsOptions
        }
      >
        <Component {...(props as P)} />
      </Elements>
    )
  }

export type SubscriptionState =
  | 'UNSUBSCRIBED'
  | 'SUBSCRIBED'
  | 'PROCESSING_PAYMENT'
  | 'PROCESSING_SUBSCRIPTION'
  | 'PAYMENT_COMPLETE'
  | 'SUBSCRIPTION_COMPLETE'

type MonthlyCreditLimitFormData = {
  monthlyCreditLimit: number
}

type PromoCodeFormData = {
  promotionalCode: string
}

type SubscribeFormData = {
  paymentMethodId: string | null
}

const SubscriptionContext = createContext<{
  continueOnPaymentComplete: () => void
  handlePaymentElementChange: (event: any) => void
  referralId: string | null
  canUsePromotionalCode: boolean
  promoCodeControl: Control<PromoCodeFormData, any>
  appliedPromotionalCode: PromotionalCode | null
  promoCodeSubmitting: boolean
  promotionalCodeBootstrapping: boolean
  onApplyPromoCode: () => Promise<void>
  onClearAppliedPromoCode: () => void
  credits: number | null
  pendingCredits: number | null
  onHandleSubmitDeleteCompanyCard: (e?: React.BaseSyntheticEvent) => Promise<void>
  onHandleSubmitSwitchCompanyCard: (e?: React.BaseSyntheticEvent) => Promise<void>
  onHandleSubmitSubscribe: (e?: React.BaseSyntheticEvent) => Promise<void>
  onHandleSubmitUnsubscribe: (e?: React.BaseSyntheticEvent) => Promise<void>
  onHandleSubmitTopup: (e?: React.BaseSyntheticEvent) => Promise<void>
  monthlyCreditLimitControl: Control<MonthlyCreditLimitFormData, any>
  subscribeControl: Control<SubscribeFormData, any>
  formStateMonthlyCreditLimit: FormState<MonthlyCreditLimitFormData>
  formStateSubscribe: FormState<SubscribeFormData>
  formStateUnsubscribe: FormState<FieldValues>
  formStateTopup: FormState<FieldValues>
  awaitingSubscription: boolean
  awaitingPayment: boolean
  subscriptionComplete: boolean
  paymentComplete: boolean
  availableTopupCredits: number
  creditsPurchasedThisMonth: number | null
  state: SubscriptionState
  processing: boolean
  subscribeDisabled: boolean
  unsubscribeDisabled: boolean
  topupDisabled: boolean
  monthlyCreditLimitDisabled: boolean
  topupError: string | undefined
  subscribeError: string | undefined
  unsubscribeError: string | undefined
  monthlyCreditLimitInForm: number | undefined
  monthlyCreditLimitError: string | undefined
  monthlyCreditLimitSubmitting: boolean
  deleteCardSubmitting: boolean
  switchCardSubmitting: boolean
  topupSubmitting: boolean
  subscribeSubmitting: boolean
  unsubscribeSubmitting: boolean
  resetSubscribeForm: UseFormReset<SubscribeFormData>
  resetMonthlyCreditLimitForm: UseFormReset<MonthlyCreditLimitFormData>
  resetUnsubscribeForm: UseFormReset<FieldValues>
  resetTopupForm: UseFormReset<FieldValues>
} | null>(null)

const useSubscriptionBase = () => {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error('no SubscriptionProvider found when calling useSubscription()')
  }
  return context
}

const useCompanyCardMenu = () => {
  const [wrappedDeleteCardSubmitting, setWrappedDeleteCardSubmitting] = useState(false)
  const [wrappedSwitchCardSubmitting, setWrappedSwitchCardSubmitting] = useState(false)
  const {
    processing,
    deleteCardSubmitting,
    onHandleSubmitDeleteCompanyCard,
    switchCardSubmitting,
    onHandleSubmitSwitchCompanyCard,
  } = useSubscriptionBase()
  const wrappedOnHandleSubmitDeleteCompanyCard: (e?: React.BaseSyntheticEvent) => Promise<void> =
    useCallback(
      event => {
        setWrappedDeleteCardSubmitting(true)
        return onHandleSubmitDeleteCompanyCard(event)
      },
      [onHandleSubmitDeleteCompanyCard],
    )
  const wrappedOnHandleSubmitSwitchCompanyCard: (e?: React.BaseSyntheticEvent) => Promise<void> =
    useCallback(
      event => {
        setWrappedSwitchCardSubmitting(true)
        return onHandleSubmitSwitchCompanyCard(event)
      },
      [onHandleSubmitSwitchCompanyCard],
    )
  useEffect(() => {
    if (!deleteCardSubmitting) {
      setWrappedDeleteCardSubmitting(false)
    }
    if (!switchCardSubmitting) {
      setWrappedSwitchCardSubmitting(false)
    }
  }, [deleteCardSubmitting, switchCardSubmitting])
  return {
    disabled: processing,
    loading: wrappedDeleteCardSubmitting || wrappedSwitchCardSubmitting,
    onHandleSubmitDeleteCompanyCard: wrappedOnHandleSubmitDeleteCompanyCard,
    onHandleSubmitSwitchCompanyCard: wrappedOnHandleSubmitSwitchCompanyCard,
  }
}
const useTopupSubscription = () => {
  const {
    onHandleSubmitTopup,
    availableTopupCredits,
    creditsPurchasedThisMonth,
    topupDisabled,
    topupSubmitting,
  } = useSubscriptionBase()
  return {
    onHandleSubmitTopup,
    availableTopupCredits,
    creditsPurchasedThisMonth,
    topupDisabled,
    topupSubmitting,
  }
}
const useSubscription = ({
  onPaymentComplete = _.noop,
  companyCards,
}: { onPaymentComplete?: () => void; companyCards?: CompanyCard[] } = {}) => {
  const subscription = useSubscriptionBase()
  const onPaymentCompleteStable = useStableCallback(onPaymentComplete)
  const onPaymentCompleteTrapRef = useRef(
    subscription.paymentComplete ||
      (subscription.subscriptionComplete && !subscription.awaitingPayment),
  )
  useEffect(() => {
    const paymentComplete =
      subscription.paymentComplete ||
      (subscription.subscriptionComplete && !subscription.awaitingPayment)
    if (!paymentComplete && onPaymentCompleteTrapRef.current) {
      onPaymentCompleteStable()
      onPaymentCompleteTrapRef.current = false
      return
    }
    if (paymentComplete) {
      onPaymentCompleteTrapRef.current = true
    }
  }, [
    onPaymentCompleteStable,
    subscription.awaitingPayment,
    subscription.paymentComplete,
    subscription.subscriptionComplete,
  ])
  // if state is subscribed on first render then continue on as you would with onPaymentComplete
  // this is an edge case when a user exits the webapp during the payment processing phase and comes back to the same stage
  useEffect(() => {
    if (subscription.state === 'SUBSCRIBED') {
      onPaymentCompleteStable()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPaymentCompleteStable])
  useEffectUntil(
    ({ stop }) => {
      if (companyCards?.length) {
        // called in a set timeout to try make sure that the paymentMethodId field is registered in the form before i set it
        setTimeout(() => {
          subscription.resetSubscribeForm(
            { paymentMethodId: companyCards[0].id },
            { keepDirtyValues: true },
          )
        })
        stop()
      }
    },
    [companyCards, subscription],
  )

  return subscription
}

const getState = ({
  hasActiveSubscription,
  awaitingSubscription,
  awaitingPayment,
  paymentComplete,
  subscriptionComplete,
}: {
  hasActiveSubscription: boolean
  awaitingSubscription: boolean
  awaitingPayment: boolean
  paymentComplete: boolean
  subscriptionComplete: boolean
}): SubscriptionState => {
  if (awaitingPayment) {
    return 'PROCESSING_PAYMENT'
  }
  if (awaitingSubscription) {
    return 'PROCESSING_SUBSCRIPTION'
  }
  if (paymentComplete) {
    return 'PAYMENT_COMPLETE'
  }
  if (subscriptionComplete) {
    return 'SUBSCRIPTION_COMPLETE'
  }
  if (hasActiveSubscription) {
    return 'SUBSCRIBED'
  }
  return 'UNSUBSCRIBED'
}

const usePaymentCompletionTracker = () => {
  const [awaitingSubscription, setAwaitingSubscription, awaitingSubscriptionRef] =
    useStateWithRef<boolean>(false)
  const [awaitingPayment, setAwaitingPayment, awaitingPaymentRef] = useStateWithRef<boolean>(false)
  const [paymentComplete, setPaymentComplete] = useState<boolean>(false)
  const [subscriptionComplete, setSubscriptionComplete] = useState<boolean>(false)
  const subscriptionAwaitingSync = useSelector(companySelectors.getSubscriptionAwaitingSync)
  const paymentAwaitingSync = useSelector(companySelectors.getPaymentAwaitingSync)
  const awaitingSyncStartedRef = useRef(false)
  useEffect(() => {
    if ((subscriptionAwaitingSync || paymentAwaitingSync) && !awaitingSyncStartedRef.current) {
      awaitingSyncStartedRef.current = true
      if (subscriptionAwaitingSync) {
        setAwaitingSubscription(true)
      }
      if (paymentAwaitingSync) {
        setAwaitingPayment(true)
      }
      setPaymentComplete(false)
      setSubscriptionComplete(false)
      return
    }
    if (awaitingSyncStartedRef.current && (!subscriptionAwaitingSync || !paymentAwaitingSync)) {
      if (!subscriptionAwaitingSync && awaitingSubscriptionRef.current) {
        setAwaitingSubscription(false)
        setSubscriptionComplete(true)
      }
      if (!paymentAwaitingSync && awaitingPaymentRef.current) {
        setAwaitingPayment(false)
        setPaymentComplete(true)
      }
      if (!awaitingSubscriptionRef.current && !awaitingPaymentRef.current) {
        awaitingSyncStartedRef.current = false
      }
    }
  }, [
    awaitingPaymentRef,
    awaitingSubscriptionRef,
    paymentAwaitingSync,
    setAwaitingPayment,
    setAwaitingSubscription,
    subscriptionAwaitingSync,
  ])
  const resetPaymentCompletionTracker = useCallback(() => {
    setAwaitingSubscription(false)
    setAwaitingPayment(false)
    setPaymentComplete(false)
    setSubscriptionComplete(false)
  }, [setAwaitingPayment, setAwaitingSubscription])
  return {
    resetPaymentCompletionTracker,
    awaitingSubscription,
    awaitingPayment,
    subscriptionComplete,
    paymentComplete,
  }
}

const SubscriptionProvider: FC<PropsWithChildren> = withStripeProvider(({ children }) => {
  const dispatch = useDispatch()
  const stripe = useStripe() as Stripe
  const elements = useElements() as StripeElements
  const { showSuccess, showError } = useSnackbar()
  const companyId = useSelector(authSelectors.getCompanyId)
  const company = useSelector(companySelectors.getCompany)
  const stripeCustomerId = company?.stripeCustomerId || null
  const canUsePromotionalCode = company?.creditsPurchasedYearMonth === null
  const monthlyCreditLimit = useSelector(companySelectors.getCompanyMonthlyCreditLimit)
  const credits = useSelector(companySelectors.getCompanyCredits)
  const creditsPurchasedMonthlyCreditLimitThisMonth = useSelectorWithMidnightRefresh(
    companySelectors.getCompanyCreditsPurchasedMonthlyCreditLimitThisMonth,
  )
  const creditsPurchasedThisMonth = useSelectorWithMidnightRefresh(
    companySelectors.getCompanyCreditsPurchasedThisMonth,
  )
  const hasActiveSubscription = useSelector(companySelectors.getHasActiveSubscription)
  const pendingCredits = useSelector(companySelectors.getCompanyPendingCredits)
  const syncClientSecret = useSelector(companySelectors.getSyncClientSecret)
  const syncIntentType = useSelector(companySelectors.getSyncIntentType)
  const syncStartTime = useSelector(companySelectors.getSyncStartTime)
  usePollAfter(
    async () => {
      if (syncIntentType && syncClientSecret) {
        switch (syncIntentType) {
          case 'payment': {
            const { paymentIntent } = await stripe.retrievePaymentIntent(syncClientSecret)
            if (
              paymentIntent?.status === 'requires_payment_method' ||
              paymentIntent?.last_payment_error?.message
            ) {
              dispatch(companyActions.paymentSyncFailed())
            } else if (paymentIntent?.status === 'succeeded') {
              const companyId = companySelectors.getCompanyId(store.getState())
              if (companyId) {
                const company = await companyService.fetchCompany({ companyId })
                dispatch(companyActions.updateCompany(company))
              }
            }
            break
          }
          case 'setup': {
            const { setupIntent } = await stripe.retrieveSetupIntent(syncClientSecret)
            if (
              setupIntent?.status === 'requires_payment_method' ||
              setupIntent?.last_setup_error?.message
            ) {
              dispatch(companyActions.paymentSyncFailed())
            } else if (setupIntent?.status === 'succeeded') {
              const companyId = companySelectors.getCompanyId(store.getState())
              if (companyId) {
                const company = await companyService.fetchCompany({ companyId })
                dispatch(companyActions.updateCompany(company))
              }
            }
            break
          }
        }
      }
    },
    syncStartTime && syncIntentType && syncClientSecret ? syncStartTime : null,
  )
  const [stripeFormCompleted, setStripeFormCompleted] = useState(false)
  const purchaseSubscription = useCallback(
    ({
      paymentMethodId,
      intentType,
      clientSecret,
    }: {
      paymentMethodId?: string
      intentType: 'payment' | 'setup'
      clientSecret: string | null
    }) => {
      dispatch(companyActions.purchaseSubscription({ paymentMethodId, intentType, clientSecret }))
    },
    [dispatch],
  )
  const cancelSubscription = useCallback(() => {
    dispatch(companyActions.cancelSubscription())
  }, [dispatch])
  const topup = useCallback(
    ({ clientSecret }: { clientSecret: string | null }) => {
      dispatch(companyActions.topup({ clientSecret }))
    },
    [dispatch],
  )
  const {
    control: subscribeControl,
    reset: resetSubscribeForm,
    handleSubmit: handleSubmitSubscribe,
    formState: formStateSubscribe,
    setError: setErrorSubscribe,
    getValues: getSubscribeFormValues,
    setValue: setSubscribeFormValue,
  } = useForm<SubscribeFormData>({
    mode: 'onChange',
    defaultValues: {
      paymentMethodId: null,
    },
  })
  const {
    reset: resetUnsubscribeForm,
    handleSubmit: handleSubmitUnsubscribe,
    formState: formStateUnsubscribe,
    setError: setErrorUnsubscribe,
  } = useForm()
  const {
    reset: resetTopupForm,
    handleSubmit: handleSubmitTopup,
    formState: formStateTopup,
    setError: setErrorTopup,
  } = useForm()
  const {
    reset: resetMonthlyCreditLimitForm,
    control: monthlyCreditLimitControl,
    watch: watchMonthlyCreditLimit,
    handleSubmit: handleSubmitMonthlyCreditLimit,
    formState: formStateMonthlyCreditLimit,
    setError: setErrorMonthlyCreditLimit,
  } = useForm<MonthlyCreditLimitFormData>({
    mode: 'onChange',
    defaultValues: {
      monthlyCreditLimit: monthlyCreditLimit || 0,
    },
  })
  const monthlyCreditLimitInForm = useWatch({
    control: monthlyCreditLimitControl,
    name: 'monthlyCreditLimit',
  })
  const paymentMethodInForm = useWatch({
    control: subscribeControl,
    name: 'paymentMethodId',
  })
  const [referralId, autoPromotionalCode] = useMemo(() => {
    if (typeof window === 'undefined') {
      return [null, null] as const
    }
    const [endorselyReferralId, endorselyPromotionalCode] =
      (window as typeof window & { endorsely_referral?: string }).endorsely_referral?.split(':') ||
      ([] as undefined[])
    return [
      endorselyReferralId || null,
      canUsePromotionalCode ? endorselyPromotionalCode || null : null,
    ] as const
  }, [canUsePromotionalCode])
  const {
    control: promoCodeControl,
    formState: formStatePromoCode,
    handleSubmit: handleSubmitPromoCode,
    setError: setErrorPromoCode,
    clearErrors: clearErrorsPromoCode,
    setValue: setPromoCodeValue,
  } = useForm<PromoCodeFormData>({
    mode: 'onChange',
    defaultValues: {
      promotionalCode: autoPromotionalCode || '',
    },
  })
  const [appliedPromotionalCode, setAppliedPromotionalCode] = useState<PromotionalCode | null>(null)
  const [promotionalCodeBootstrapping, setPromotionalCodeBootstrapping] =
    useState(!!autoPromotionalCode)
  const autoPromotionalCodeAppliedRef = useRef(false)
  const onSubmitPromoCode: SubmitHandler<PromoCodeFormData> = useCallback(
    async values => {
      const promotionalCode = values.promotionalCode?.trim()
      if (!promotionalCode) {
        setErrorPromoCode('promotionalCode', {
          type: 'validate',
          message: 'Please enter a promotional code.',
        })
        return
      }
      if (!company) {
        setErrorPromoCode('promotionalCode', {
          type: 'validate',
          message: 'Could not validate promotional code. Please try again.',
        })
        return
      }
      await promoCodeForm
        .submitPromoCodeValidation({
          promotionalCode,
          company,
        })
        .then(result => {
          if (!result.valid) {
            setErrorPromoCode('promotionalCode', {
              type: 'validate',
              message: promoCodeForm.getValidationErrorMessage(result.reason),
            })
            return
          }
          clearErrorsPromoCode('promotionalCode')
          setAppliedPromotionalCode(result.promotionalCode)
        })
        .catch((error: Error) => {
          setErrorPromoCode('promotionalCode', {
            type: 'validate',
            message: error.message || 'Could not validate promotional code. Please try again.',
          })
        })
    },
    [clearErrorsPromoCode, company, setErrorPromoCode],
  )
  const stableHandleSubmitPromoCode = useStableCallback(handleSubmitPromoCode(onSubmitPromoCode))
  const onApplyPromoCode = stableHandleSubmitPromoCode
  const onClearAppliedPromoCode = useCallback(() => {
    setAppliedPromotionalCode(null)
    clearErrorsPromoCode('promotionalCode')
    setPromoCodeValue('promotionalCode', '')
  }, [clearErrorsPromoCode, setPromoCodeValue])
  useEffect(() => {
    if (autoPromotionalCodeAppliedRef.current || !autoPromotionalCode || !company) {
      return
    }
    autoPromotionalCodeAppliedRef.current = true
    void onApplyPromoCode().finally(() => {
      setPromotionalCodeBootstrapping(false)
    })
  }, [autoPromotionalCode, company, onApplyPromoCode])
  useEffect(() => {
    if (!autoPromotionalCode) {
      setPromotionalCodeBootstrapping(false)
    }
  }, [autoPromotionalCode])
  const availableTopupCredits = useMemo(() => {
    if (!creditsPurchasedMonthlyCreditLimitThisMonth) {
      return 0
    }
    const amountOffset = Math.max(creditsPurchasedMonthlyCreditLimitThisMonth || 0, credits || 0, 0)
    const availableTopupCredits = Math.max((monthlyCreditLimitInForm || 0) - amountOffset, 0)
    return availableTopupCredits
  }, [credits, creditsPurchasedMonthlyCreditLimitThisMonth, monthlyCreditLimitInForm])
  const {
    resetPaymentCompletionTracker,
    awaitingSubscription,
    awaitingPayment,
    subscriptionComplete,
    paymentComplete,
  } = usePaymentCompletionTracker()
  const state = getState({
    awaitingSubscription,
    awaitingPayment,
    hasActiveSubscription,
    paymentComplete,
    subscriptionComplete,
  })
  const { formState: formStateDeleteCompanyCard, submit: stableHandleSubmitDeleteCompanyCard } =
    useDeleteCompanyCardForm({
      onSubmitSuccess: (companyCardId: string) => {
        if (state === 'UNSUBSCRIBED') {
          const { paymentMethodId } = getSubscribeFormValues()
          if (companyCardId === paymentMethodId) {
            setSubscribeFormValue('paymentMethodId', null)
          }
        }
        showSuccess('Successfully deleted card')
      },
      onSubmitError: error => {
        showError(error)
      },
    })
  const { formState: formStateSwitchCompanyCard, submit: stableHandleSubmitSwitchCompanyCard } =
    useSwitchCompanyCardForm({
      onSubmitSuccess: () => {
        showSuccess('Successfully updated subscription')
      },
      onSubmitError: error => {
        showError(error)
      },
    })
  const onSubmitMonthlyCreditLimit: SubmitHandler<MonthlyCreditLimitFormData> =
    useCallbackDebouncedAsync(
      async ({ monthlyCreditLimit }) => {
        if (!companyId) {
          throw new Error('Failed to submit. Please try again.')
        }
        await monthlyCreditLimitForm
          .onSubmit({ companyId, monthlyCreditLimit })
          .then(() => {
            dispatch(companyActions.updateCompanyMonthlyCreditLimit(monthlyCreditLimit))
          })
          .catch((error: Error) => {
            setErrorMonthlyCreditLimit('root', { message: error.message })
          })
      },
      [companyId, dispatch, setErrorMonthlyCreditLimit],
      600,
    )
  const onSubmitSubscribe: SubmitHandler<SubscribeFormData> = useCallback(
    async ({ paymentMethodId }) => {
      try {
        if (!stripe) {
          setErrorSubscribe('root', {
            message: 'Payment form is not ready. Please try again shortly.',
          })
          return
        }
        const companyId = companySelectors.getCompanyId(store.getState())
        if (!companyId) {
          setErrorSubscribe('root', {
            message: 'Payment form is not ready. Please reload the page and try again.',
          })
          return
        }
        const company = await companyService.fetchCompany({ companyId })
        const currentCompany = companySelectors.getCompany(store.getState())
        dispatch(companyActions.companyLoaded(company))
        if (!company.stripeCustomerId) {
          setErrorSubscribe('root', {
            message: 'Payment setup is still loading. Please reload the page and try again.',
          })
          return
        }
        if (
          currentCompany &&
          (company.credits !== currentCompany.credits ||
            company.creditsPurchased !== currentCompany.creditsPurchased ||
            company.creditsPurchasedYearMonth !== currentCompany.creditsPurchasedYearMonth)
        ) {
          setErrorSubscribe('root', {
            message:
              'Credit data was stale. Please review the information on screen and try again.',
          })
          return
        }
        const { intentType, status, clientSecret } = paymentMethodId
          ? await subscribeForm.onSubmitExistingPaymentMethod({
              paymentMethodId,
              stripe,
            })
          : await subscribeForm.onSubmitNewCard({
              elements,
              stripe,
              referralId: referralId || undefined,
              promotionalCode: appliedPromotionalCode?.id,
            })
        purchaseSubscription({
          paymentMethodId:
            status === 'succeeded_existing_payment_method'
              ? paymentMethodId || undefined
              : undefined,
          intentType,
          clientSecret,
        })
        setAppliedPromotionalCode(null)
        clearErrorsPromoCode('promotionalCode')
        setPromoCodeValue('promotionalCode', '')
      } catch (error) {
        setErrorSubscribe('root', {
          message:
            error instanceof Error ? error.message : 'Something went wrong. Please try again.',
        })
      }
    },
    [
      stripe,
      dispatch,
      elements,
      setErrorSubscribe,
      purchaseSubscription,
      referralId,
      appliedPromotionalCode,
      clearErrorsPromoCode,
      setPromoCodeValue,
    ],
  )
  const onSubmitUnsubscribe = useCallback(async () => {
    const companyId = companySelectors.getCompanyId(store.getState())
    if (!companyId) {
      setErrorUnsubscribe('root', {
        message: 'Unsubscribe form is not ready. Please reload the page and try again.',
      })
      return
    }
    await unsubscribeForm
      .onSubmit({ companyId })
      .then(() => {
        cancelSubscription()
        resetUnsubscribeForm()
      })
      .catch((error: Error) => {
        setErrorUnsubscribe('root', { message: error.message })
      })
  }, [setErrorUnsubscribe, cancelSubscription, resetUnsubscribeForm])
  const onSubmitTopup = useCallback(async () => {
    try {
      const companyId = companySelectors.getCompanyId(store.getState())
      if (!companyId) {
        setErrorSubscribe('root', {
          message: 'Payment form is not ready. Please reload the page and try again.',
        })
        return
      }
      const company = await companyService.fetchCompany({ companyId })
      const currentCompany = companySelectors.getCompany(store.getState())
      dispatch(companyActions.companyLoaded(company))
      if (
        currentCompany &&
        (company.credits !== currentCompany.credits ||
          company.creditsPurchased !== currentCompany.creditsPurchased ||
          company.creditsPurchasedYearMonth !== currentCompany.creditsPurchasedYearMonth)
      ) {
        setErrorSubscribe('root', {
          message: 'Credit data was stale. Please review the information on screen and try again.',
        })
        return
      }
      await topupForm.onSubmit().then(({ clientSecret }) => {
        topup({ clientSecret })
        resetTopupForm()
      })
    } catch (error) {
      setErrorTopup('root', {
        message: error instanceof Error ? error.message : 'Something went wrong. Please try again.',
      })
    }
  }, [dispatch, resetTopupForm, setErrorSubscribe, setErrorTopup, topup])
  const onHandleSubmitMonthlyCreditLimit = useStableCallback(
    handleSubmitMonthlyCreditLimit(onSubmitMonthlyCreditLimit),
  )
  const stableHandleSubmitSubscribe = useStableCallback(handleSubmitSubscribe(onSubmitSubscribe))
  const stableHandleSubmitUnsubscribe = useStableCallback(
    handleSubmitUnsubscribe(onSubmitUnsubscribe),
  )
  const stableHandleSubmitTopup = useStableCallback(handleSubmitTopup(onSubmitTopup))
  const onHandleSubmitSubscribe = useCallbackDebounced(stableHandleSubmitSubscribe, [
    stableHandleSubmitSubscribe,
  ])
  const onHandleSubmitUnsubscribe = useCallbackDebounced(stableHandleSubmitUnsubscribe, [
    stableHandleSubmitUnsubscribe,
  ])
  const onHandleSubmitTopup = useCallbackDebounced(stableHandleSubmitTopup, [
    stableHandleSubmitTopup,
  ])
  const onHandleSubmitDeleteCompanyCard = useCallbackDebounced(
    stableHandleSubmitDeleteCompanyCard,
    [stableHandleSubmitDeleteCompanyCard],
  )
  const onHandleSubmitSwitchCompanyCard = useCallbackDebounced(
    stableHandleSubmitSwitchCompanyCard,
    [stableHandleSubmitSwitchCompanyCard],
  )
  useAutoSave({
    watch: watchMonthlyCreditLimit,
    submit: onHandleSubmitMonthlyCreditLimit,
  })
  const continueOnPaymentComplete = useStableCallback(() => {
    // trigger resetSubscribeForm when the payment animation completes to set the subscription page to display the new subscription
    if (paymentComplete || (subscriptionComplete && !awaitingPayment)) {
      resetPaymentCompletionTracker()
      resetSubscribeForm()
    }
    if (paymentComplete) {
      resetPaymentCompletionTracker()
      resetTopupForm()
    }
  })
  const handlePaymentElementChange = useCallback((event: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    setStripeFormCompleted(!!event.complete)
  }, [])
  const value = useMemo(
    () => ({
      referralId,
      canUsePromotionalCode,
      promoCodeControl,
      appliedPromotionalCode,
      promoCodeSubmitting: formStatePromoCode.isSubmitting,
      promotionalCodeBootstrapping,
      onApplyPromoCode,
      onClearAppliedPromoCode,
      credits,
      pendingCredits,
      onHandleSubmitDeleteCompanyCard,
      onHandleSubmitSwitchCompanyCard,
      onHandleSubmitSubscribe,
      onHandleSubmitUnsubscribe,
      onHandleSubmitTopup,
      monthlyCreditLimitControl,
      subscribeControl,
      formStateMonthlyCreditLimit,
      formStateSubscribe,
      formStateUnsubscribe,
      formStateTopup,
      awaitingSubscription,
      awaitingPayment,
      subscriptionComplete,
      paymentComplete,
      availableTopupCredits,
      creditsPurchasedThisMonth,
      monthlyCreditLimitInForm,
      state,
      processing:
        !stripe ||
        promotionalCodeBootstrapping ||
        formStatePromoCode.isSubmitting ||
        formStateDeleteCompanyCard.isSubmitting ||
        formStateSwitchCompanyCard.isSubmitting ||
        formStateSubscribe.isSubmitting ||
        formStateUnsubscribe.isSubmitting ||
        formStateTopup.isSubmitting ||
        formStateMonthlyCreditLimit.isSubmitting ||
        state === 'PROCESSING_PAYMENT' ||
        state === 'PAYMENT_COMPLETE' ||
        state === 'SUBSCRIPTION_COMPLETE' ||
        state === 'PROCESSING_SUBSCRIPTION',
      subscribeDisabled:
        (!stripeFormCompleted && !paymentMethodInForm) ||
        promotionalCodeBootstrapping ||
        formStatePromoCode.isSubmitting ||
        !monthlyCreditLimitInForm ||
        !stripe ||
        !stripeCustomerId ||
        formStateDeleteCompanyCard.isSubmitting ||
        formStateSwitchCompanyCard.isSubmitting ||
        formStateSubscribe.isSubmitting ||
        formStateMonthlyCreditLimit.isSubmitting ||
        !!formStateMonthlyCreditLimit.errors.root?.message ||
        state === 'PROCESSING_PAYMENT' ||
        state === 'PAYMENT_COMPLETE' ||
        state === 'SUBSCRIPTION_COMPLETE' ||
        state === 'PROCESSING_SUBSCRIPTION',
      unsubscribeDisabled:
        !stripe ||
        formStateDeleteCompanyCard.isSubmitting ||
        formStateSwitchCompanyCard.isSubmitting ||
        formStateUnsubscribe.isSubmitting ||
        formStateTopup.isSubmitting ||
        formStateMonthlyCreditLimit.isSubmitting ||
        !!formStateMonthlyCreditLimit.errors.root?.message ||
        state === 'PROCESSING_PAYMENT' ||
        state === 'PAYMENT_COMPLETE' ||
        state === 'SUBSCRIPTION_COMPLETE' ||
        state === 'PROCESSING_SUBSCRIPTION',
      topupDisabled:
        !stripe ||
        formStateDeleteCompanyCard.isSubmitting ||
        formStateSwitchCompanyCard.isSubmitting ||
        formStateUnsubscribe.isSubmitting ||
        formStateTopup.isSubmitting ||
        formStateMonthlyCreditLimit.isSubmitting ||
        !!formStateMonthlyCreditLimit.errors.root?.message ||
        state === 'PROCESSING_PAYMENT' ||
        state === 'PAYMENT_COMPLETE' ||
        state === 'SUBSCRIPTION_COMPLETE' ||
        state === 'PROCESSING_SUBSCRIPTION',
      monthlyCreditLimitDisabled:
        formStateSubscribe.isSubmitting ||
        formStateUnsubscribe.isSubmitting ||
        formStateTopup.isSubmitting ||
        state === 'PROCESSING_PAYMENT' ||
        state === 'PAYMENT_COMPLETE' ||
        state === 'SUBSCRIPTION_COMPLETE' ||
        state === 'PROCESSING_SUBSCRIPTION',
      topupError: formStateTopup.errors.root?.message,
      subscribeError: formStateSubscribe.errors.root?.message,
      unsubscribeError: formStateUnsubscribe.errors.root?.message,
      monthlyCreditLimitError: formStateMonthlyCreditLimit.errors.root?.message,
      monthlyCreditLimitSubmitting: formStateMonthlyCreditLimit.isSubmitting,
      topupSubmitting: formStateTopup.isSubmitting,
      subscribeSubmitting: formStateSubscribe.isSubmitting,
      unsubscribeSubmitting: formStateUnsubscribe.isSubmitting,
      deleteCardSubmitting: formStateDeleteCompanyCard.isSubmitting,
      switchCardSubmitting: formStateSwitchCompanyCard.isSubmitting,
      handlePaymentElementChange,
      continueOnPaymentComplete,
      resetSubscribeForm,
      resetUnsubscribeForm,
      resetTopupForm,
      resetMonthlyCreditLimitForm,
    }),
    [
      referralId,
      canUsePromotionalCode,
      appliedPromotionalCode,
      promotionalCodeBootstrapping,
      onApplyPromoCode,
      onClearAppliedPromoCode,
      availableTopupCredits,
      creditsPurchasedThisMonth,
      awaitingPayment,
      awaitingSubscription,
      continueOnPaymentComplete,
      credits,
      formStateDeleteCompanyCard.isSubmitting,
      formStateMonthlyCreditLimit,
      formStatePromoCode.isSubmitting,
      formStateSubscribe,
      formStateSwitchCompanyCard.isSubmitting,
      formStateTopup,
      formStateUnsubscribe,
      handlePaymentElementChange,
      monthlyCreditLimitControl,
      monthlyCreditLimitInForm,
      promoCodeControl,
      onHandleSubmitDeleteCompanyCard,
      onHandleSubmitSubscribe,
      onHandleSubmitSwitchCompanyCard,
      onHandleSubmitTopup,
      onHandleSubmitUnsubscribe,
      paymentComplete,
      paymentMethodInForm,
      pendingCredits,
      resetMonthlyCreditLimitForm,
      resetSubscribeForm,
      resetTopupForm,
      resetUnsubscribeForm,
      state,
      stripe,
      stripeCustomerId,
      stripeFormCompleted,
      subscribeControl,
      subscriptionComplete,
    ],
  )
  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
})

export { useSubscription, useTopupSubscription, useCompanyCardMenu, SubscriptionProvider }
export default SubscriptionProvider
